import { Suspense, lazy, useEffect, useState } from "react";
import { Switch, Route, useLocation, useParams } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "@/components/error-boundary";
import { ReconnectingBanner } from "@/components/reconnecting-banner";
import { Skeleton } from "@/components/ui/skeleton";
import NotFound from "@/pages/not-found";
import { AuthProvider, useAuth } from "@/lib/auth";

import { AppLayout } from "@/components/layout/app-layout";
import { AdminGuard } from "@/components/admin-guard";
import { PublicLayoutWrapper } from "@/components/home/public-layout-wrapper";

const PublicHome = lazy(() => import("@/pages/public/home"));

const SessionView = lazy(() => import("@/pages/public/session-view"));
const ProfilePage = lazy(() => import("@/pages/public/profile"));
const PricingPage = lazy(() => import("@/pages/public/pricing"));
const TermsPage = lazy(() => import("@/pages/public/terms"));
const PrivacyPage = lazy(() => import("@/pages/public/privacy"));
const AIDisclosurePage = lazy(() => import("@/pages/public/ai-disclosure"));
const ReviewsPage = lazy(() => import("@/pages/public/reviews"));
const BenchmarksPage = lazy(() => import("@/pages/public/benchmarks"));
const MemoryPage = lazy(() => import("@/pages/public/memory"));
const EvaluatePage = lazy(() => import("@/pages/public/evaluate"));
const VerifyPage = lazy(() => import("@/pages/public/verify"));
const ShareVerifyPage = lazy(() => import("@/pages/public/share-verify"));
const CertifiedRecordPage = lazy(() => import("@/pages/public/certified-record"));
const LoginPage = lazy(() => import("@/pages/public/login"));
const RegisterPage = lazy(() => import("@/pages/public/register"));
const ForgotPasswordPage = lazy(() => import("@/pages/public/forgot-password"));
const ResetPasswordPage = lazy(() => import("@/pages/public/reset-password"));
const InvitePreviewPage = lazy(() => import("@/pages/public/invite-preview"));
const WasteReportPage = lazy(() => import("@/pages/public/waste-report"));
const SessionHistoryPage = lazy(() => import("@/pages/public/session-history"));
const SessionAnalyticsPage = lazy(() => import("@/pages/public/session-analytics"));
const PatentValidationPage = lazy(() => import("@/pages/public/patent-validation"));
const FAQPage = lazy(() => import("@/pages/public/faq"));
const PublicResponsePreview = lazy(() => import("@/pages/preview/public-response-preview"));

const Dashboard = lazy(() => import("@/pages/dashboard"));
const Sessions = lazy(() => import("@/pages/sessions"));
const SessionRunner = lazy(() => import("@/pages/session-runner"));
const AuditLogs = lazy(() => import("@/pages/audit-logs"));
const TestRun = lazy(() => import("@/pages/test-run"));
// Task #1186 (T005): each admin route is its own dynamic import so visiting
// /admin/operations doesn't pull every other admin page into the same chunk.
// Heavy routes (Operations, BenchmarkHarness, IPAssessment, IntelligenceHub,
// AdminBenchmarks, etc.) now load on demand independently.
const OutputBundle = lazy(() => import("@/pages/admin/output-bundle"));
const IntelligenceHub = lazy(() => import("@/pages/admin/intelligence-hub"));
const BillingHub = lazy(() => import("@/pages/admin/billing-hub"));
const RequestsHub = lazy(() => import("@/pages/admin/requests-hub"));
const SessionsHub = lazy(() => import("@/pages/admin/sessions-hub"));
const InsightsHub = lazy(() => import("@/pages/admin/insights-hub"));
const SettingsHub = lazy(() => import("@/pages/admin/settings-hub"));
const DownloadsHub = lazy(() => import("@/pages/admin/downloads-hub"));
const AdminBenchmarks = lazy(() => import("@/pages/admin/benchmarks"));
const OperationsDashboard = lazy(() => import("@/pages/admin/operations-dashboard"));
const LiveEvaluation = lazy(() => import("@/pages/admin/evaluation"));
const HardeningDashboard = lazy(() => import("@/pages/admin/hardening"));
const BenchmarkHarness = lazy(() => import("@/pages/admin/benchmark-harness"));
const AdminReports = lazy(() => import("@/pages/admin/reports"));
const AdminReviews = lazy(() => import("@/pages/admin/reviews"));
const AdminFeedback = lazy(() => import("@/pages/admin/feedback"));
const IPAssessment = lazy(() => import("@/pages/admin/ip-assessment"));
const AdesMonitor = lazy(() => import("@/pages/admin/ades-monitor"));
const PatentPanel = lazy(() => import("@/pages/admin/patent-panel"));
const ResolverTelemetry = lazy(() => import("@/pages/admin/resolver-telemetry"));
const OutreachTemplates = lazy(() => import("@/pages/admin/outreach-templates"));

function PageLoadingFallback() {
  return (
    <div className="space-y-6 p-6 animate-pulse" data-testid="page-loading-fallback">
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-lg" />
        <div className="space-y-2">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-3 w-64" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-24 rounded-xl" />
      </div>
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );
}

function Redirect({ to }: { to: string }) {
  const [, setLocation] = useLocation();
  useEffect(() => { setLocation(to, { replace: true }); }, [to, setLocation]);
  return null;
}

function AdminPanel() {
  return (
    <AdminGuard>
      <AppLayout>
        <ErrorBoundary fallbackTitle="Admin panel error" showHomeButton={true}>
          <Suspense fallback={<PageLoadingFallback />}>
            <Switch>
              <Route path="/" component={Dashboard} />
              <Route path="/sessions/:id/bundle" component={OutputBundle} />
              <Route path="/sessions/:id" component={SessionRunner} />
              <Route path="/sessions" component={SessionsHub} />
              <Route path="/insights" component={InsightsHub} />
              <Route path="/intelligence">{() => <IntelligenceHub />}</Route>
              <Route path="/downloads" component={DownloadsHub} />
              <Route path="/requests" component={RequestsHub} />
              <Route path="/billing" component={BillingHub} />
              <Route path="/settings" component={SettingsHub} />
              <Route path="/benchmarks" component={AdminBenchmarks} />
              <Route path="/operations" component={OperationsDashboard} />
              <Route path="/evaluation" component={LiveEvaluation} />
              <Route path="/hardening" component={HardeningDashboard} />
              <Route path="/benchmark-harness" component={BenchmarkHarness} />
              <Route path="/reports" component={AdminReports} />
               <Route path="/reviews" component={AdminReviews} />
               <Route path="/feedback" component={AdminFeedback} />
              <Route path="/ip-assessment" component={IPAssessment} />
              <Route path="/ades-monitor" component={AdesMonitor} />
              <Route path="/patent" component={PatentPanel} />
              <Route path="/resolver-telemetry" component={ResolverTelemetry} />
              <Route path="/outreach" component={OutreachTemplates} />
              <Route path="/invites">{() => <Redirect to="/requests#invites" />}</Route>
              <Route path="/usage">{() => <Redirect to="/billing#usage" />}</Route>
              <Route path="/test-run" component={TestRun} />

              <Route path="/audit-logs">{() => <Redirect to="/sessions#activity" />}</Route>
              <Route path="/analytics">{() => <Redirect to="/insights#analytics" />}</Route>
              <Route path="/forensic-audit">{() => <Redirect to="/insights#compare" />}</Route>
              <Route path="/protocols">{() => <Redirect to="/settings#rules" />}</Route>
              <Route path="/protocol-shield">{() => <Redirect to="/settings#protection" />}</Route>
              <Route path="/explain">{() => <Redirect to="/insights#summary" />}</Route>
              <Route component={NotFound} />
            </Switch>
          </Suspense>
        </ErrorBoundary>
      </AppLayout>
    </AdminGuard>
  );
}


function ReferralRedirect() {
  const { slug } = useParams<{ slug: string }>();
  const [, setLocation] = useLocation();
  useEffect(() => {
    if (slug) {
      fetch(`/api/public/referral/${encodeURIComponent(slug)}`)
        .then(res => {
          if (res.ok) {
            sessionStorage.setItem("referralSlug", slug);
          }
        })
        .catch(() => {})
        .finally(() => setLocation("/", { replace: true }));
    } else {
      setLocation("/", { replace: true });
    }
  }, [slug, setLocation]);
  return null;
}

function EmailVerificationBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  if (!user || user.emailVerified || dismissed) return null;
  return (
    <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-2 text-center text-sm text-amber-300" data-testid="banner-email-unverified">
      Your email is not verified. Please check your inbox or{" "}
      <a href="/profile" className="underline font-medium hover:text-amber-200">visit your profile</a>{" "}
      to resend the verification email.
      <button onClick={() => setDismissed(true)} className="ml-3 text-amber-400 hover:text-amber-200" data-testid="button-dismiss-email-banner">&times;</button>
    </div>
  );
}

function AppRouter() {
  return (
    <>
      <EmailVerificationBanner />
      <Suspense fallback={<PageLoadingFallback />}>
        <Switch>
          <Route path="/" component={PublicHome} />
          <Route path="/login" component={LoginPage} />
          <Route path="/register" component={RegisterPage} />
          <Route path="/forgot-password" component={ForgotPasswordPage} />
          <Route path="/reset-password" component={ResetPasswordPage} />
          <Route path="/reviews">{() => <PublicLayoutWrapper><ReviewsPage /></PublicLayoutWrapper>}</Route>
          <Route path="/benchmarks">{() => <PublicLayoutWrapper><BenchmarksPage /></PublicLayoutWrapper>}</Route>
          <Route path="/pricing">{() => <PublicLayoutWrapper><PricingPage /></PublicLayoutWrapper>}</Route>
          <Route path="/terms" component={TermsPage} />
          <Route path="/privacy" component={PrivacyPage} />
          <Route path="/ai-disclosure" component={AIDisclosurePage} />
          <Route path="/profile">{() => <PublicLayoutWrapper><ProfilePage /></PublicLayoutWrapper>}</Route>
          <Route path="/memory">{() => <PublicLayoutWrapper><MemoryPage /></PublicLayoutWrapper>}</Route>
          <Route path="/session/:id/view">{() => <PublicLayoutWrapper><SessionView /></PublicLayoutWrapper>}</Route>
          <Route path="/verify">{() => <PublicLayoutWrapper><VerifyPage /></PublicLayoutWrapper>}</Route>
          <Route path="/v/:verificationId">{() => <PublicLayoutWrapper><ShareVerifyPage /></PublicLayoutWrapper>}</Route>
          <Route path="/r/:recordId" component={CertifiedRecordPage} />
          <Route path="/preview/public-response" component={PublicResponsePreview} />
          <Route path="/evaluate/:token" component={EvaluatePage} />
          <Route path="/invite/preview/:code" component={InvitePreviewPage} />
          <Route path="/refer/:slug" component={ReferralRedirect} />
          <Route path="/report/waste" component={WasteReportPage} />
          <Route path="/history">{() => <PublicLayoutWrapper><SessionHistoryPage /></PublicLayoutWrapper>}</Route>
          <Route path="/analytics">{() => <PublicLayoutWrapper><SessionAnalyticsPage /></PublicLayoutWrapper>}</Route>
          <Route path="/patent-validation">{() => <PublicLayoutWrapper><PatentValidationPage /></PublicLayoutWrapper>}</Route>
          <Route path="/faq" component={FAQPage} />
          <Route path="/analyze">{() => <Redirect to="/" />}</Route>
          <Route path="/compare">{() => <PublicLayoutWrapper><PublicHome /></PublicLayoutWrapper>}</Route>
          <Route path="/dashboard">{() => <Redirect to="/" />}</Route>
          <Route path="/execution">{() => <Redirect to="/" />}</Route>
          <Route path="/admin" nest component={AdminPanel} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <ReconnectingBanner />
          <Toaster />
          <ErrorBoundary fallbackTitle="Application error" showHomeButton={true}>
            <AppRouter />
          </ErrorBoundary>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
